import { Link, createFileRoute } from '@tanstack/react-router'
import { Table } from '@mantine/core'
import { fetchDevices } from '../../admin/server-fns'
import {
  AdminRouteError,
  PageHeader,
  Panel,
  StatusBadge,
  formatDate,
  kickerCls,
  tdCls,
  thCls,
} from '../../admin/ui'

const STATUSES = ['active', 'provisioning', 'suspended', 'revoked', 'offline'] as const

export const Route = createFileRoute('/admin/')({
  loader: async () => {
    const [recent, ...counts] = await Promise.all([
      fetchDevices({ data: { page: 1, limit: 8 } }),
      ...STATUSES.map((status) => fetchDevices({ data: { page: 1, limit: 1, status } })),
    ])
    return {
      recent,
      counts: Object.fromEntries(STATUSES.map((s, i) => [s, counts[i].total])) as Record<
        (typeof STATUSES)[number],
        number
      >,
    }
  },
  component: Dashboard,
  errorComponent: AdminRouteError,
})

const tileCls =
  'block no-underline transition hover:-translate-y-0.5 hover:[&>div]:border-blue-700 dark:hover:[&>div]:border-blue-400'

function Dashboard() {
  const { recent, counts } = Route.useLoaderData()

  return (
    <>
      <PageHeader kicker="Fleet" title="Dashboard" />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Link to="/admin/devices" className={tileCls}>
          <Panel className="p-4">
            <p className={`${kickerCls} mb-2`}>Total</p>
            <p className="m-0 font-mono text-2xl font-semibold text-slate-900 dark:text-white">
              {recent.total}
            </p>
          </Panel>
        </Link>
        {STATUSES.map((status) => (
          <Link key={status} to="/admin/devices" search={{ status }} className={tileCls}>
            <Panel className="p-4">
              <p className={`${kickerCls} mb-2`}>{status}</p>
              <p className="m-0 flex items-baseline gap-2 font-mono text-2xl font-semibold text-slate-900 dark:text-white">
                {counts[status]}
                <StatusBadge status={status} />
              </p>
            </Panel>
          </Link>
        ))}
      </div>

      <Panel>
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <p className={`${kickerCls} m-0`}>Recent devices</p>
          <Link
            to="/admin/devices"
            className="text-xs font-semibold text-blue-700 no-underline hover:underline dark:text-blue-400"
          >
            All devices →
          </Link>
        </div>
        {recent.items.length === 0 ? (
          <p className="m-0 px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
            No devices yet. Add one on the Devices page to see it here.
          </p>
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th className={thCls}>Serial</Table.Th>
                  <Table.Th className={thCls}>Status</Table.Th>
                  <Table.Th className={thCls}>Firmware</Table.Th>
                  <Table.Th className={thCls}>Last seen</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recent.items.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>{d.serialNumber}</Table.Td>
                    <Table.Td className={tdCls}>
                      <StatusBadge status={d.status} />
                    </Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>
                      {d.firmwareVersion ?? '—'}
                    </Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>
                      {formatDate(d.lastSeenAt)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Panel>
    </>
  )
}
